import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ChevronRight, ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { SiteFooter } from "@/components/SiteFooter";
import { useLang } from "@/context/LangContext";
import { useSeo, SITE } from "@/lib/seo";
import { HUBS, HUB_REDIRECTS, hubForPackage } from "@/lib/seoHubs";
import { cityContent, fitTitle, fitDescription } from "@/lib/seoCity";
import { AuthorBio } from "@/pages/HubPage";
import PAGES from "@/lib/seoPages.json";
import { NeuralBackground } from "@/components/home/NeuralBackground";

const href = (p) => HUB_REDIRECTS[p.slug] || p.route || `/servizi/${p.slug}`;

const Index = () => {
  const groups = [["servizi", "Servizi"], ["citta", "Per città"], ["settori", "Per settore"]];
  return (
    <div className="space-y-10">
      <h1 className="font-display text-4xl sm:text-5xl">Servizi di marketing digitale in Ticino</h1>
      <section>
        <h2 className="text-base md:text-lg font-semibold mb-3">Servizi principali</h2>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {HUBS.map((h) => <li key={h.slug}><Link to={`/${h.slug}`} data-testid={`seo-hub-${h.slug}`} className="block border-2 border-[#2B3036] bg-white px-3 py-2 text-sm font-medium hover:border-[#E0262C]">{h.title.split(" | ")[0]}</Link></li>)}
        </ul>
      </section>
      {groups.map(([g, label]) => (
        <section key={g}>
          <h2 className="text-base md:text-lg font-semibold mb-3">{label}</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {PAGES.filter((p) => p.group === g).map((p) => (
              <li key={p.slug}><Link to={href(p)} data-testid={`seo-link-${p.slug}`} className="block border border-[#cfd6d9] bg-white px-3 py-2 text-sm hover:border-[#2B3036]">{p.title}</Link></li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

const detailLd = (p) => [
  { "@context": "https://schema.org", "@type": "Service", name: p.title, description: p.description, areaServed: p.city || "Ticino, Svizzera",
    provider: { "@type": "LocalBusiness", name: "AURORA-ADV · PIXAWEB Sagl", address: { "@type": "PostalAddress", streetAddress: "Via Cantonale 19", postalCode: "6900", addressLocality: "Lugano", addressRegion: "TI", addressCountry: "CH" }, telephone: "+41764411359", email: "developers@graficawebdesign.com" } },
  { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Servizi", item: `${SITE}/servizi` },
    { "@type": "ListItem", position: 3, name: p.title, item: `${SITE}/servizi/${p.slug}` }] },
];

const CitySections = ({ c }) => (
  <div data-testid="seo-city-content" className="mt-10 space-y-8">
    {c.sections.map((s) => (
      <section key={s.h2}>
        <h2 className="font-display text-2xl tracking-tight">{s.h2}</h2>
        <p className="mt-2 text-[15px] leading-relaxed text-[#3f454c]">{s.p}</p>
      </section>
    ))}
  </div>
);

const Detail = ({ p }) => {
  const related = PAGES.filter((x) => x.package === p.package && x.slug !== p.slug).slice(0, 6);
  const city = cityContent(p);
  const hub = city || (hubForPackage(p.package) && { hub: `/${hubForPackage(p.package).slug}`, hubLabel: hubForPackage(p.package).title.split(" | ")[0] });
  return (
    <article data-testid="seo-landing" className="max-w-3xl">
      <h1 data-testid="seo-landing-title" className="font-display text-4xl sm:text-5xl leading-tight">{p.title}</h1>
      <p className="text-base md:text-lg text-[#5b6168] mt-4">{p.description}</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/" data-testid="seo-landing-cta" className="h-11 px-5 inline-flex items-center gap-2 bg-[#2B3036] text-white text-sm hover:bg-[#E0262C] transition-colors">Analizza gratis il tuo sito <ArrowRight className="w-4 h-4" /></Link>
        {p.package && <Link to={`/#package-${p.package}`} className="h-11 px-5 inline-flex items-center border border-[#2B3036] text-sm hover:bg-white">Vedi pacchetti e prezzi</Link>}
      </div>
      {city && <CitySections c={city} />}
      {hub && <p className="mt-8 text-sm">Approfondisci: <Link to={hub.hub} data-testid="seo-hub-link" className="underline hover:text-[#E0262C]">{hub.hubLabel}</Link></p>}
      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="text-base md:text-lg font-semibold mb-3">Pagine correlate</h2>
          <ul className="flex flex-wrap gap-2">{related.map((r) => <li key={r.slug}><Link to={href(r)} className="text-xs border border-[#cfd6d9] bg-white px-3 py-1.5 hover:border-[#2B3036]">{r.title}</Link></li>)}</ul>
        </section>
      )}
      <AuthorBio />
    </article>
  );
};

export default function ServiceLanding() {
  const { slug } = useParams();
  const { lang, setLang, t } = useLang();
  const p = slug && PAGES.find((x) => x.slug === slug);
  useSeo(p ? { title: fitTitle(p.title), description: fitDescription(p.description), path: `/servizi/${p.slug}`, jsonld: detailLd(p), lang: "it" }
    : { title: "Servizi di marketing digitale in Ticino | AURORA-ADV", description: fitDescription("Tutti i servizi AURORA-ADV per città e settore in Ticino e Svizzera italiana: SEO locale, campagne ADV, social, web/app e audit gratuito."), path: "/servizi", noindex: !!slug, lang: "it" });
  useEffect(() => { window.scrollTo(0, 0); }, [p]);
  if (HUB_REDIRECTS[slug]) return <Navigate to={HUB_REDIRECTS[slug]} replace />;
  return (
    <div className="min-h-screen bg-[#E6EAEC] flex flex-col">
      <Header lang={lang} setLang={setLang} t={t} />
      <div className="relative flex-1 overflow-hidden">
      <NeuralBackground />
      <main className="relative max-w-6xl mx-auto w-full px-6 lg:px-10 py-12">
        <nav data-testid="breadcrumb" aria-label="breadcrumb" className="flex items-center gap-1 text-xs text-[#71717a] mb-8">
          <Link to="/" className="hover:text-[#2B3036]">Home</Link><ChevronRight className="w-3 h-3" />
          <Link to="/servizi" className="hover:text-[#2B3036]">Servizi</Link>
          {p && <><ChevronRight className="w-3 h-3" /><span className="text-[#2B3036]">{p.title}</span></>}
        </nav>
        {slug && !p ? <p data-testid="seo-not-found">Pagina non trovata. <Link to="/servizi" className="underline">Tutti i servizi</Link></p> : p ? <Detail p={p} /> : <Index />}
      </main>
      </div>
      <SiteFooter />
    </div>
  );
}
